import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import AboutUS from './features/about/AboutUS';
import NotFoundPage from './pages/NotFoundPage';
import './styles/main.sass';
import Header from 'src/components/navigations/Header';
import Footer from 'src/components/navigations/Footer';
import Login from './features/access/login/Login';
import SignUp from './features/access/signup/SignUp';
import News from './features/xnews/News';
import { useAppDispatch } from 'src/store/reducers/store';
import { login } from 'src/store/reducers/authSlice';
import NewsDetail from 'src/features/xnews/NewsDetail';
import Test from './pages/Test';
import { useHreflangTags } from './components/seo/hreflang';
import { TranslationProvider, useTranslations } from './contexts/TranslationContext';
import { getCurrentLanguageFromCookies } from './utils/navigationApi';
import Archive from 'src/features/xnews/Archive';
import ArchiveCategoryByMonth from './features/xnews/ArchiveCategoryByMonth';
import Contact from './features/about/Contact';
import Privacy from './features/about/Privacy';
import Terms from './features/about/Terms';
import ArchiveMonthsByCategory from './features/xnews/ArchiveMonthsByCategory';
import HtmlLangSetter from './components/seo/HtmlLangSetter';



const languages = ['en', 'cn', 'tw', 'es', 'fr'];



const RoutesWithTranslations: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { language, forceReload } = useTranslations();
    const { updateHreflangTags } = useHreflangTags(language);

    // Extract lang directly from path (e.g., "/en/news/...")
    const langFromPath = location.pathname.split('/')[1];
    const lang = languages.includes(langFromPath) ? langFromPath : 'en';

    //This ensures the language is saved as a cookie for future visits.
    useEffect(() => {
        if (language) {
            document.cookie = `lang=${language}; path=/; max-age=31536000`;
        }
    }, [language]);

    // Force reload translations on initial mount
    useEffect(() => {
        console.log('[RootComponent] Reloading translations on app start');
        forceReload().catch(error => {
            console.error('[RootComponent] Error reloading translations:', error);
        });
        // We only want to run this once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        updateHreflangTags();
    }, [location, language, updateHreflangTags]);

    const handleLogin = (userData: { email: string; name: string }) => {
        console.log('root dispatch login userData', userData);
        dispatch(login(userData));
    };

    // Handler for locale changes - not needed since we use TranslationContext
    const handleLocaleChange = (newLocale: string) => {
        console.log(`[RootComponent] Locale change handler called with: ${newLocale}`);
        // Implementation handled by TranslationContext
    };


    return (
        <div className="min-h-screen bg-white text-black">
            <HtmlLangSetter lang={lang} />
            <Header setLocale={handleLocaleChange} />

            <Routes>
           
                <Route path="/" element={<LanguageBasedRedirect />} />

                {languages.map(lang => (
                    <Route key={lang} path={`/${lang}`} element={<Navigate to={`/${lang}/news/list/trending`} replace />} />
                ))}

                <Route path="/:lang/about/AboutUS" element={<AboutUS />} />

                {/* Contact routes */}
                <Route path="/:lang/about/contact" element={<Contact />} />

                {/* Privacy routes */}
                <Route path="/:lang/about/privacy" element={<Privacy />} />

                {/* Terms routes */}
                <Route path="/:lang/about/terms" element={<Terms />} />

                <Route path="/test" element={<Test lang="tw" />} />

                <Route path="/:lang/user/login" element={<Login onLogin={handleLogin} />} />

                <Route path="/:lang/user/signup" element={<SignUp onLogin={handleLogin} />} />

                {/* News detail route remains unchanged */}
                <Route path="/:lang/news/trendingnews/:id" element={<NewsDetail />} />

                {/* News list route with optional subtype */}
                <Route path="/:lang/news/list/:type/:subtype?" element={<News />} />

                {/* Archive Routes */}
                <Route path="/:lang/news/archive" element={<Archive />} />

                <Route path="/:lang/news/archive/category/:category" element={<ArchiveMonthsByCategory />} />

                <Route path="/:lang/news/archive/category/:category/:yyyymm" element={<ArchiveCategoryByMonth />} />



                {/* this should stay at end of route */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>

            <Footer />
        </div>
    );
};

const LanguageBasedRedirect: React.FC = () => {
    useEffect(() => {
        // Redirect user based on cookie or browser language
        const langCookie = getCurrentLanguageFromCookies();
        if (langCookie && languages.includes(langCookie)) {
            window.location.replace(`/${langCookie}/news/list/trending`);
        } else {
            // fallback to English
            window.location.replace(`/en/news/list/trending`);
        }
    }, []);
    return null;
};

const RootComponent: React.FC = () => {
    return (
        <TranslationProvider>
            <RoutesWithTranslations />
        </TranslationProvider>
    );
};

export default RootComponent;
